import { memo, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';

import { ExcelPresentationTableProvider } from './ExcelPresentationTableContext';
import ExcelPresentationTableRaw from './ExcelPresentationTableRaw';

interface ExcelPresentationTableProps {
  csvFile: any;
}

const ExcelPresentationTable: React.FC<ExcelPresentationTableProps> = memo(
  ({ csvFile }) => {
    const [rawTableData, setRawTableData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const file = csvFile?.[0];

      if (!file) {
        setError('No file selected');
        setIsLoading(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;

          if (!data) {
            setError('Failed to read file');
            setIsLoading(false);
            return;
          }

          // Parse the file using xlsx
          const workbook = XLSX.read(data, { type: 'binary' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON with headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false,
          });

          if (!jsonData || jsonData.length === 0) {
            setError('No data found in file');
            setIsLoading(false);
            return;
          }

          // Convert array of arrays to array of objects
          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1) as any[][];

          const formattedData = rows.map((row) => {
            const obj: any = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] !== undefined ? row[index] : '';
            });
            return obj;
          });

          setRawTableData(formattedData);
          setError(null);
          setIsLoading(false);
        } catch (err) {
          console.error('Error parsing file:', err);
          setError(
            'Error parsing file. Please ensure it is a valid CSV or Excel file.'
          );
          setIsLoading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setIsLoading(false);
      };

      // Read the file
      reader.readAsBinaryString(file);
    }, [csvFile]);

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (error) {
      return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
    }

    return (
      <ExcelPresentationTableProvider>
        <ExcelPresentationTableRaw rawTableData={rawTableData} />
      </ExcelPresentationTableProvider>
    );
  }
);

export default ExcelPresentationTable;
